import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { claim as applyClaim, canClaim, undoClaim, type ClaimOutcome } from '../game/claim';
import {
  endTrip, newTrip, setActiveTrip, startTrip, upgradeLegacyBoards,
} from '../game/trips';
import { activeTrip, emptyPlayerState, type PlayerState } from '../game/types';
import { generateId, publicIdFor } from '../game/codes';
import { mergeStates } from '../game/merge';
import { API } from '../api/types';
import {
  clearAuthLink, clearRecoveryCode, getAuthLink, getFriendsCache, getRecoveryCode,
  loadOrCreate, localStorageAdapter, setAuthLink as storeAuthLink,
  setFriendsCache as storeFriendsCache, setRecoveryCode,
} from '../storage/local';
import { SyncClient, attachSyncTriggers } from '../storage/syncClient';
import type { AuthLink, FriendsCache, SyncState } from '../storage/types';

/**
 * Single owner of the player's state.
 *
 * The state lives in a ref as well as in React state: the ref is what the sync
 * client reads, so a push always sees the newest snapshot rather than whatever
 * React last rendered. Every mutation goes through `commit`, which persists to
 * IndexedDB and schedules a debounced sync - gameplay itself never awaits
 * either.
 */

export interface LastClaim {
  plateId: string;
  points: number;
  creditedTrip: boolean;
  creditedLifetime: boolean;
  at: number;
}

/**
 * A sign-in that found an existing collection, waiting on the player to say what
 * to do with the one already on this device.
 *
 * Held here rather than in the Settings screen because the choice mutates
 * identity, and identity has exactly one owner.
 */
export interface PendingAdoption {
  playerId: string;
  payload: PlayerState;
  email: string | null;
}

/**
 * What a completed sign-in did, for the Settings screen to report.
 *
 * `choice` is the only one that leaves anything outstanding - it means
 * `pendingAdoption` is now set and the player has to resolve it.
 */
export type SignInResult =
  | { kind: 'linked' }
  | { kind: 'adopted' }
  | { kind: 'choice' }
  | { kind: 'conflict' };

/** The friends board and code, as the current player last saw them. */
export type FriendsView = Omit<FriendsCache, 'playerId'>;

const NO_FRIENDS: FriendsView = { code: null, board: [], at: 0 };

interface GameContextValue {
  ready: boolean;
  state: PlayerState;
  /**
   * How this player appears on every leaderboard - see `publicIdFor`. Null only
   * for the moment it takes to hash, so a board highlights nobody meanwhile.
   */
  publicId: string | null;
  sync: SyncState;
  recoveryCode: string | null;
  authLink: AuthLink | null;
  pendingAdoption: PendingAdoption | null;
  lastClaim: LastClaim | null;
  dismissLastClaim: () => void;

  friends: FriendsView;
  /** Fetches and caches the friend code if this device has not got it. Needs signal. */
  ensureFriendCode: () => Promise<void>;
  addFriend: (friendCode: string) => Promise<void>;
  removeFriend: (publicId: string) => Promise<void>;

  claim: (plateId: string) => ClaimOutcome | null;
  undo: (plateId: string) => void;
  canClaim: (plateId: string) => boolean;

  beginTrip: (name: string) => void;
  finishTrip: (tripId: string) => void;
  switchTrip: (tripId: string | null) => void;
  joinTrip: (joinCode: string) => Promise<void>;

  setDisplayName: (name: string) => void;
  restoreFrom: (recoveryCode: string) => Promise<void>;
  resetEverything: () => Promise<void>;
  syncNow: () => Promise<void>;

  beginSignIn: () => void;
  completeSignIn: (ticket: string, email: string | null) => Promise<SignInResult>;
  resolveSignIn: (choice: 'merge' | 'switch') => Promise<void>;
  cancelSignIn: () => void;
  unlinkAccount: () => Promise<void>;
}

const GameContext = createContext<GameContextValue | null>(null);

export function useGame(): GameContextValue {
  const value = useContext(GameContext);
  if (!value) throw new Error('useGame must be used inside <GameProvider>');
  return value;
}

export function GameProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PlayerState | null>(null);
  const [recoveryCode, setCode] = useState<string | null>(null);
  const [authLink, setLink] = useState<AuthLink | null>(null);
  const [pendingAdoption, setPendingAdoption] = useState<PendingAdoption | null>(null);
  const [lastClaim, setLastClaim] = useState<LastClaim | null>(null);
  const [sync, setSync] = useState<SyncState>({
    status: 'local-only', lastSyncedAt: null, lastError: null,
  });
  const [publicId, setPublicId] = useState<string | null>(null);
  const [friendsCache, setFriendsCacheState] = useState<FriendsCache | null>(null);

  const stateRef = useRef<PlayerState | null>(null);
  const clientRef = useRef<SyncClient | null>(null);
  // Mirrors friendsCache for the same reason stateRef mirrors state: a sync
  // response lands outside React's render cycle and must extend the newest copy.
  const friendsRef = useRef<FriendsCache | null>(null);

  const persist = useCallback((next: PlayerState) => {
    stateRef.current = next;
    setState(next);
    void localStorageAdapter.save(next);
  }, []);

  /**
   * Records friends data for one player. A cache held for any other player is
   * dropped rather than extended, since it belongs to an identity this device no
   * longer is.
   */
  const saveFriends = useCallback((
    playerId: string,
    patch: Partial<FriendsView>,
  ) => {
    const base = friendsRef.current?.playerId === playerId
      ? friendsRef.current
      : { playerId, ...NO_FRIENDS };
    const next: FriendsCache = { ...base, ...patch, playerId };
    friendsRef.current = next;
    setFriendsCacheState(next);
    void storeFriendsCache(next);
  }, []);

  const commit = useCallback((next: PlayerState) => {
    persist(next);
    clientRef.current?.schedule();
  }, [persist]);

  // ------------------------------------------------------------- bootstrap
  useEffect(() => {
    let cancelled = false;

    const client = new SyncClient({
      getState: () => stateRef.current ?? emptyPlayerState(generateId()),
      onMerged: (merged) => persist(merged),
      onStatus: (next) => setSync(next),
      onRegistered: async (playerId, code) => {
        await setRecoveryCode(code);
        if (cancelled) return;
        setCode(code);
        const current = stateRef.current;
        // Adopt the server-issued id before the push reads the snapshot.
        if (current) persist({ ...current, playerId });
      },
      getStoredRecoveryCode: getRecoveryCode,
      // Another tab won the registration race and wrote the real player id to
      // IndexedDB. Re-read it so this tab syncs as that player rather than
      // stranding itself on the local id it invented before registering.
      onAdoptedExisting: async () => {
        const [stored, code] = await Promise.all([
          localStorageAdapter.load(),
          getRecoveryCode(),
        ]);
        if (cancelled) return;
        if (code) setCode(code);
        if (stored) persist(stored);
      },
      // The server owns trip ids and join codes, so a locally-created trip
      // adopts whatever came back - the proposed code may have been taken.
      onTripRegistered: (localTripId, serverTripId, joinCode) => {
        const current = stateRef.current;
        if (!current) return;
        persist({
          ...current,
          trips: current.trips.map((t) =>
            t.id === localTripId
              ? { ...t, id: serverTripId, joinCode, registered: true }
              : t,
          ),
          activeTripId:
            current.activeTripId === localTripId ? serverTripId : current.activeTripId,
        });
      },
      onLeaderboard: (tripId, leaderboard) => {
        const current = stateRef.current;
        if (!current) return;
        persist({
          ...current,
          trips: current.trips.map((t) =>
            t.id === tripId ? { ...t, leaderboard, leaderboardAt: Date.now() } : t,
          ),
        });
      },
      onFriends: (playerId, board) => {
        // A board for an identity this device has since left is somebody else's.
        if (stateRef.current?.playerId !== playerId) return;
        saveFriends(playerId, { board, at: Date.now() });
      },
    });
    clientRef.current = client;

    void (async () => {
      const [loaded, code, link] = await Promise.all([
        loadOrCreate(), getRecoveryCode(), getAuthLink(),
      ]);
      // Loaded before the first flush, so the board that flush brings back
      // extends this cache rather than racing it and dropping the friend code.
      const friends = await getFriendsCache(loaded.playerId);
      if (cancelled) return;

      stateRef.current = loaded;
      setState(loaded);
      setCode(code);
      setLink(link);
      friendsRef.current = friends;
      setFriendsCacheState(friends);
      // A device that arrived here by signing in has no recovery code - the
      // server only ever held its hash - so the link is the other proof of
      // registration. Testing the code alone would send this device off to mint
      // a second player and strand the collection it just adopted.
      if (code || link) client.markRegistered();

      // Pull anything the server has that this device missed.
      void client.flush();
    })();

    const detach = attachSyncTriggers(client);
    return () => {
      cancelled = true;
      detach();
    };
  }, [persist, saveFriends]);

  // Registration, restore and sign-in all change the player id, and with it the
  // public id every board knows this player by.
  const currentPlayerId = state?.playerId;
  useEffect(() => {
    if (!currentPlayerId) return;
    let cancelled = false;
    void publicIdFor(currentPlayerId).then((id) => {
      if (!cancelled) setPublicId(id);
    });
    return () => {
      cancelled = true;
    };
  }, [currentPlayerId]);

  // ----------------------------------------------------------------- actions
  const doClaim = useCallback((plateId: string): ClaimOutcome | null => {
    const current = stateRef.current;
    if (!current) return null;

    const outcome = applyClaim(current, plateId);
    if (outcome.state === current) return null; // nothing credited

    commit(outcome.state);
    setLastClaim({
      plateId,
      points: outcome.points,
      creditedTrip: outcome.creditedTrip,
      creditedLifetime: outcome.creditedLifetime,
      at: Date.now(),
    });

    // A short buzz is the only feedback that survives road noise and sunlight,
    // and a trip-only re-claim needs it most - that is the moment you are least
    // likely to be looking at the screen. The double pattern marks a plate that
    // is new to the lifetime collection, so the two outcomes are told apart
    // without looking.
    if ('vibrate' in navigator) {
      navigator.vibrate?.(outcome.creditedLifetime ? [12, 40, 22] : 12);
    }
    return outcome;
  }, [commit]);

  const doUndo = useCallback((plateId: string) => {
    const current = stateRef.current;
    if (!current) return;
    commit(undoClaim(current, plateId));
    setLastClaim(null);
  }, [commit]);

  const beginTrip = useCallback((name: string) => {
    const current = stateRef.current;
    if (!current) return;
    commit(startTrip(current, { name }));
    // Push straight away so the join code becomes real and shareable, rather
    // than waiting out the debounce with a code nobody can actually use.
    void clientRef.current?.flush();
  }, [commit]);

  const finishTrip = useCallback((tripId: string) => {
    const current = stateRef.current;
    if (!current) return;
    commit(endTrip(current, tripId));
    // Ending a trip is a natural "done for now" moment, so push immediately.
    void clientRef.current?.flush();
  }, [commit]);

  const switchTrip = useCallback((tripId: string | null) => {
    const current = stateRef.current;
    if (current) commit(setActiveTrip(current, tripId));
  }, [commit]);

  const joinTrip = useCallback(async (joinCode: string) => {
    const client = clientRef.current;
    const current = stateRef.current;
    if (!client || !current) throw new Error('Not ready yet');

    const result = await client.joinTrip(joinCode);

    // Already in this trip (rejoining on a new phone, say) - just make it active.
    const existing = current.trips.find((t) => t.id === result.tripId);
    if (existing) {
      commit({ ...current, activeTripId: result.tripId, version: current.version + 1 });
      return;
    }

    const trip = newTrip({
      name: result.name,
      id: result.tripId,
      joinCode: result.joinCode,
      registered: true,
      isOwner: false,
      now: result.startedAt,
    });
    commit({
      ...current,
      trips: [{ ...trip, leaderboard: result.leaderboard, leaderboardAt: Date.now() }, ...current.trips],
      activeTripId: result.tripId,
      version: current.version + 1,
    });
  }, [commit]);

  const setDisplayName = useCallback((name: string) => {
    const current = stateRef.current;
    if (!current) return;
    const trimmed = name.trim().slice(0, 40) || 'Player';
    if (trimmed === current.displayName) return;
    commit({ ...current, displayName: trimmed, version: current.version + 1 });
  }, [commit]);

  const restoreFrom = useCallback(async (code: string) => {
    const client = clientRef.current;
    if (!client) throw new Error('Sync is not ready yet');

    const result = await client.restore(code);
    const payload = await upgradeLegacyBoards(result.payload);
    await setRecoveryCode(code.toLowerCase());
    setCode(code.toLowerCase());
    client.markRegistered();
    persist({ ...payload, playerId: result.playerId });
  }, [persist]);

  // ------------------------------------------------------------ single sign-on
  //
  // Signing in is strictly additive: it links an account to the player this
  // device already is, or it hands the device a player it can prove it owns.
  // Nothing about gameplay, sync or offline play depends on any of it.

  const beginSignIn = useCallback(() => {
    const current = stateRef.current;
    if (!current) return;
    // A full navigation, not a fetch: the browser has to visit Google's consent
    // screen and come back. Anything unsaved is already in IndexedDB.
    window.location.assign(API.authStart('google', current.playerId));
  }, []);

  /**
   * Takes on a collection proven by a sign-in.
   *
   * `payload` is null when the account has linked but never synced - in which
   * case there is nothing to take on, and this is only an id change.
   */
  const applyAdoption = useCallback(async (
    playerId: string,
    payload: PlayerState | null,
    email: string | null,
    choice: 'merge' | 'switch',
  ) => {
    // The account's saved copy may predate public ids. Upgraded before `current`
    // is read, so nothing the player does during the await is lost.
    const remote = payload ? await upgradeLegacyBoards(payload) : null;
    const current = stateRef.current;
    if (!current) return;

    let next: PlayerState;
    if (!remote) {
      next = { ...current, playerId, version: current.version + 1 };
    } else if (choice === 'merge') {
      // mergeStates deliberately keeps local.playerId (see merge.ts) so that a
      // routine sync can never change identity. This is the one path where it
      // must, so the override is explicit rather than a quirk of the merge.
      next = { ...mergeStates(current, remote), playerId };
    } else {
      next = { ...remote, playerId };
    }

    // The stored code belongs to the player just left behind; keeping it would
    // offer this device a code that opens somebody else's collection.
    await clearRecoveryCode();
    const link: AuthLink = { provider: 'google', email };
    await storeAuthLink(link);

    setCode(null);
    setLink(link);
    setPendingAdoption(null);
    persist(next);

    clientRef.current?.markRegistered();
    // Any trip carried in by a merge was registered under the *old* player, so
    // this player is not a participant in it. Those trips stay as local history
    // and their leaderboards stop updating - the plates still count.
    void clientRef.current?.flush();
  }, [persist]);

  const completeSignIn = useCallback(async (
    ticket: string,
    email: string | null,
  ): Promise<SignInResult> => {
    const client = clientRef.current;
    const current = stateRef.current;
    if (!client || !current) throw new Error('Not ready yet');

    const result = await client.exchangeAuthTicket(ticket);

    if (result.outcome === 'conflict') return { kind: 'conflict' };

    if (result.outcome === 'linked') {
      const link: AuthLink = { provider: result.provider, email };
      await storeAuthLink(link);
      setLink(link);
      client.markRegistered();
      return { kind: 'linked' };
    }

    // Adoption. Only ask when there is genuinely something to lose: a fresh
    // device, or an account that has never synced, has one obvious answer.
    const localPlates = Object.keys(current.lifetime).length;
    if (!result.payload || localPlates === 0) {
      await applyAdoption(result.playerId, result.payload ?? null, email, 'switch');
      return { kind: 'adopted' };
    }

    setPendingAdoption({ playerId: result.playerId, payload: result.payload, email });
    return { kind: 'choice' };
  }, [applyAdoption]);

  const resolveSignIn = useCallback(async (choice: 'merge' | 'switch') => {
    const pending = pendingAdoption;
    if (!pending) return;
    await applyAdoption(pending.playerId, pending.payload, pending.email, choice);
  }, [pendingAdoption, applyAdoption]);

  /**
   * Backs out of an adoption. Safe by construction: the callback wrote nothing
   * for this case, so there is no server-side state to undo.
   */
  const cancelSignIn = useCallback(() => setPendingAdoption(null), []);

  const unlinkAccount = useCallback(async () => {
    const client = clientRef.current;
    const current = stateRef.current;
    if (!client || !current) throw new Error('Not ready yet');

    await client.unlinkAuth(current.playerId);
    await clearAuthLink();
    setLink(null);
  }, []);

  const resetEverything = useCallback(async () => {
    await localStorageAdapter.clear();
    const fresh = emptyPlayerState(generateId());
    setCode(null);
    setLink(null);
    setPendingAdoption(null);
    friendsRef.current = null;
    setFriendsCacheState(null);
    persist(fresh);
  }, [persist]);

  const syncNow = useCallback(async () => {
    await clientRef.current?.flush();
  }, []);

  // ------------------------------------------------------------------ friends
  //
  // Each of these needs a server-side player to exist, so a device that has
  // never had signal registers first rather than being told it is unknown. The
  // board they return replaces the cached one straight away; after that it
  // refreshes on every sync, like a trip's.

  const ensureFriendCode = useCallback(async () => {
    const client = clientRef.current;
    const cached = friendsRef.current;
    if (!client || !stateRef.current) return;
    if (cached?.code && cached.playerId === stateRef.current.playerId) return;

    await client.ensureRegistered();
    // Read after registering, because a first registration is what changes it.
    const playerId = stateRef.current?.playerId;
    if (!playerId) return;
    const { friendCode } = await client.getFriendCode();
    saveFriends(playerId, { code: friendCode });
  }, [saveFriends]);

  const addFriend = useCallback(async (friendCode: string) => {
    const client = clientRef.current;
    if (!client || !stateRef.current) throw new Error('Not ready yet');

    await client.ensureRegistered();
    const playerId = stateRef.current?.playerId;
    if (!playerId) throw new Error('Not ready yet');
    const { friends } = await client.addFriend(friendCode);
    saveFriends(playerId, { board: friends, at: Date.now() });
  }, [saveFriends]);

  const removeFriend = useCallback(async (friendPublicId: string) => {
    const client = clientRef.current;
    const playerId = stateRef.current?.playerId;
    if (!client || !playerId) throw new Error('Not ready yet');

    // No registration step: a device with friends to remove is registered.
    const { friends } = await client.removeFriend(friendPublicId);
    saveFriends(playerId, { board: friends, at: Date.now() });
  }, [saveFriends]);

  const value = useMemo<GameContextValue | null>(() => {
    if (!state) return null;
    // Checked here as well as on write, so an identity change hides the old
    // player's board on the very next render rather than after the next sync.
    const friends = friendsCache?.playerId === state.playerId
      ? { code: friendsCache.code, board: friendsCache.board, at: friendsCache.at }
      : NO_FRIENDS;
    return {
      ready: true,
      state,
      publicId,
      sync,
      recoveryCode,
      authLink,
      pendingAdoption,
      lastClaim,
      dismissLastClaim: () => setLastClaim(null),
      friends,
      ensureFriendCode,
      addFriend,
      removeFriend,
      claim: doClaim,
      undo: doUndo,
      canClaim: (plateId: string) => canClaim(state, plateId),
      beginTrip,
      finishTrip,
      switchTrip,
      joinTrip,
      setDisplayName,
      restoreFrom,
      resetEverything,
      syncNow,
      beginSignIn,
      completeSignIn,
      resolveSignIn,
      cancelSignIn,
      unlinkAccount,
    };
  }, [
    state, publicId, sync, recoveryCode, authLink, pendingAdoption, lastClaim, doClaim, doUndo,
    friendsCache, ensureFriendCode, addFriend, removeFriend,
    beginTrip, finishTrip, switchTrip, joinTrip, setDisplayName,
    restoreFrom, resetEverything, syncNow,
    beginSignIn, completeSignIn, resolveSignIn, cancelSignIn, unlinkAccount,
  ]);

  if (!value) {
    return <div className="empty">Loading your collection…</div>;
  }

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

/** Convenience for screens that care which ledger is being credited. */
export function useActiveTrip() {
  const { state } = useGame();
  return activeTrip(state);
}
